import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';

import { UserRole } from '../generated/graphql';
import { useTravelerSelfCheck } from '../hooks/useTravelerSelfCheck';
import { useUserRole } from '../hooks/useUserRole';

import Card from './Card';
import Loading from './Loading';

type TravelerOrAdminRouteProps = {
  children: ReactNode;
};

export default function TravelerOrAdminRoute({
  children,
}: TravelerOrAdminRouteProps) {
  const { roles, isRolesLoaded } = useUserRole();
  const { isOnlyTraveler, hasLinkedProfile } = useTravelerSelfCheck();

  // Wait for roles to be loaded before making access decisions
  // This prevents briefly showing protected content during auth state loading
  if (!isRolesLoaded) {
    return <Loading />;
  }

  // Include ClientReadOnly as they have elevated permissions over pure travelers
  const isAdminOrUser = roles?.some((role) =>
    [
      UserRole.ClientAdmin,
      UserRole.ClientUser,
      UserRole.ClientReadOnly,
    ].includes(role),
  );

  // Admins and users always have access
  if (isAdminOrUser) {
    return <>{children}</>;
  }

  // Travelers with linked profile have access
  if (isOnlyTraveler && hasLinkedProfile) {
    return <>{children}</>;
  }

  // Travelers without linked profile see warning
  if (isOnlyTraveler && !hasLinkedProfile) {
    return (
      <Card>
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">Account Not Linked</p>
            <p className="mt-1 text-sm text-yellow-700">
              Your account is not linked to a team member profile. Please
              contact your administrator to create reservations or request
              changes.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // Read-only or other users see not authorized
  return (
    <Card>
      <div>Not Authorized!</div>
    </Card>
  );
}
